import { type AppLocale, resolveLocale } from '@kcm/contracts'

const LANG_COOKIE = 'kcm_lang'

/** Countries whose residents get a non-English launch locale by default. */
const COUNTRY_LOCALE: Record<string, AppLocale> = {
  TH: 'th',
  VN: 'vi',
  TW: 'tw',
  HK: 'tw',
  MO: 'tw',
  CN: 'cn',
  JP: 'ja',
  KR: 'ko',
}

/** ISO 3166-1 alpha-2 → launch locale, or null when English is the right default. */
export function localeForCountry(country: string): AppLocale | null {
  return COUNTRY_LOCALE[country.trim().toUpperCase()] ?? null
}

export function hasExplicitLocale(): boolean {
  return Boolean(new URLSearchParams(window.location.search).get('lang') || readLangCookie())
}

export function readLangCookie(): string | null {
  const m = document.cookie.match(/(?:^|;\s*)kcm_lang=([^;]+)/)
  return m?.[1] ? decodeURIComponent(m[1]) : null
}

export function writeLangCookie(lang: string): void {
  // A preference, not a credential: host-only, a year, Lax.
  document.cookie = `${LANG_COOKIE}=${encodeURIComponent(lang)}; Path=/; Max-Age=31536000; SameSite=Lax`
}

/**
 * ?lang= → cookie → browser → site default, constrained to what the site
 * enables (PLAN.md §17). Only a hint that resolves to an enabled locale wins;
 * anything else falls through to the next hint.
 */
export function resolveInitialLocale(enabled: AppLocale[], fallback: AppLocale): AppLocale {
  const url = new URLSearchParams(window.location.search).get('lang')
  for (const hint of [url, readLangCookie(), navigator.language]) {
    if (!hint) continue
    const loc = resolveLocale(hint, fallback)
    if (enabled.includes(loc)) return loc
  }
  return enabled.includes(fallback) ? fallback : (enabled[0] ?? fallback)
}

/**
 * Per-locale font class. Plus Jakarta Sans has no Thai or CJK coverage, so
 * those locales need a covering fallback or they render in a system default
 * that breaks the brand (PLAN.md §17).
 */
export function fontClassFor(locale: string): string {
  if (locale === 'th') return 'font-thai'
  if (locale === 'ja') return 'font-jp'
  if (locale === 'tw') return 'font-tc'
  if (locale === 'ko') return 'font-kr'
  return ''
}
